import React from 'react';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { PromoCodeRow } from '../types';
import { AppliedToBadge } from './AppliedToBadge';
import { FilterButton } from './FilterButton';

type StatusFilter = 'all' | 'active' | 'inactive';

export function PromoCodesTable({
  promoCodeRows,
  isPromotionsLoading,
  onStatusChange
}: {
  promoCodeRows: PromoCodeRow[];
  isPromotionsLoading: boolean;
  onStatusChange: (promotionId: string, isActive: boolean) => Promise<boolean>;
}) {
  const [openActionMenu, setOpenActionMenu] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [updatingPromotionId, setUpdatingPromotionId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState('');

  const visiblePromoCodeRows = promoCodeRows.filter((promoCode) => {
    if (statusFilter === 'active') return promoCode.isActive;
    if (statusFilter === 'inactive') return !promoCode.isActive;

    return true;
  });
  const emptyStateMessage =
    statusFilter === 'active'
      ? 'No active promo codes found'
      : statusFilter === 'inactive'
      ? 'No deactivated promo codes found'
      : 'No promo codes found';

  React.useEffect(() => {
    if (!openActionMenu) return;

    function handleDocumentMouseDown(event: MouseEvent) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (target.closest('[data-promo-action-trigger], [data-promo-action-menu]')) return;

      setOpenActionMenu(null);
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [openActionMenu]);

  function handleStatusFilterChange(nextStatusFilter: StatusFilter) {
    setStatusFilter(nextStatusFilter);
    setOpenActionMenu(null);
  }

  async function handleStatusChange(promoCode: PromoCodeRow) {
    setActionError('');
    setUpdatingPromotionId(promoCode.id);

    const wasUpdated = await onStatusChange(promoCode.id, !promoCode.isActive);

    setUpdatingPromotionId(null);
    setOpenActionMenu(null);

    if (!wasUpdated) {
      setActionError(
        `Could not ${promoCode.isActive ? 'deactivate' : 'activate'} ${
          promoCode.code
        }. Please try again.`
      );
    }
  }

  return (
    <section className="min-h-[calc(100vh-190px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-end border-b border-gray-200 px-6 py-5">
        <div className="flex flex-wrap items-center justify-end gap-4">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => handleStatusFilterChange('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'active'}
            onClick={() => handleStatusFilterChange('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            active={statusFilter === 'inactive'}
            onClick={() => handleStatusFilterChange('inactive')}
          >
            Deactivated
          </FilterButton>
        </div>
      </div>

      {actionError ? (
        <div className="border-b border-red-100 bg-red-50 px-6 py-4 text-lg font-gilroy-medium text-red-600">
          {actionError}
        </div>
      ) : null}

      <div className="overflow-x-auto pb-40">
        <table className="w-full min-w-[1250px] border-separate border-spacing-0 text-left">
          <thead>
            <tr className="border-b border-gray-200 text-xl font-gilroy-bold text-neutral-700">
              <th className="border-b border-gray-200 px-10 py-7 font-semibold">Promo code</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Discount value</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Start date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">End date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Applied to</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Status</th>
              <th className="border-b border-gray-200 px-10 py-7 text-center font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isPromotionsLoading ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={7}
                >
                  Loading promo codes...
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading && !visiblePromoCodeRows.length ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={7}
                >
                  {emptyStateMessage}
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading &&
              visiblePromoCodeRows.map((promoCode) => (
                <tr key={promoCode.id} className="text-2xl font-gilroy-medium text-neutral-700">
                  <td className="whitespace-nowrap px-10 py-9 align-top">{promoCode.code}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">
                    {promoCode.discountValue}
                  </td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.startDate}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.endDate}</td>
                  <td className="px-6 py-8 align-top">
                    <div className="flex flex-wrap items-center gap-3">
                      {(promoCode.appliedTo.length ? promoCode.appliedTo : ['All']).map((item) => (
                        <AppliedToBadge key={item}>{item}</AppliedToBadge>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-8 align-top">
                    <span
                      className={`inline-flex items-center rounded-xl px-6 py-3 text-lg font-gilroy-bold leading-none shadow-sm ring-1 ${
                        promoCode.isActive
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                          : 'bg-rose-50 text-rose-600 ring-rose-100'
                      }`}
                    >
                      {promoCode.status}
                    </span>
                  </td>
                  <td className="relative px-10 py-7 text-center align-top">
                    <button
                      data-promo-action-trigger
                      type="button"
                      aria-label={`Open actions for ${promoCode.code}`}
                      aria-expanded={openActionMenu === promoCode.id}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-900 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      onClick={() =>
                        setOpenActionMenu((currentOpenMenu) =>
                          currentOpenMenu === promoCode.id ? null : promoCode.id
                        )
                      }
                    >
                      <MoreVertIcon className="!h-7 !w-7" />
                    </button>

                    {openActionMenu === promoCode.id ? (
                      <div
                        data-promo-action-menu
                        className="absolute right-7 top-20 z-30 w-[260px] rounded-xl border border-gray-100 bg-white p-2 text-left shadow-[0_12px_28px_rgba(15,23,42,0.12)]"
                      >
                        <button
                          type="button"
                          disabled={updatingPromotionId === promoCode.id}
                          className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-lg font-gilroy-bold transition focus:outline-none focus-visible:ring-2 disabled:cursor-wait disabled:opacity-60 ${
                            promoCode.isActive
                              ? 'text-red-500 hover:bg-red-50 hover:text-red-600 focus-visible:bg-red-50 focus-visible:ring-red-100'
                              : 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 focus-visible:bg-emerald-50 focus-visible:ring-emerald-100'
                          }`}
                          onClick={() => handleStatusChange(promoCode)}
                        >
                          {promoCode.isActive ? (
                            <BlockIcon className="!h-6 !w-6" />
                          ) : (
                            <CheckCircleOutlineIcon className="!h-6 !w-6" />
                          )}
                          <span>
                            {updatingPromotionId === promoCode.id
                              ? 'Updating...'
                              : promoCode.isActive
                              ? 'Deactivate promo code'
                              : 'Activate promo code'}
                          </span>
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
